const SALT_LENGTH = 16; // bytes
const IV_LENGTH = 12; // bytes (96 bits is standard for GCM)
const KEY_LENGTH = 32; // bytes (256 bits)
const PBKDF2_ITERATIONS = 210_000;
const PBKDF2_HASH = "SHA-256";

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Get Web Crypto in any environment (browser or Node >=18)
const getCrypto = (): Crypto => {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.subtle) {
    return globalThis.crypto as Crypto;
  }
  throw new Error(
    "Web Crypto API (crypto.subtle) not available in this environment",
  );
};

// base64url helpers (Uint8Array <-> string)
const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const base64UrlDecode = (str: string): Uint8Array => {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// Derive AES key from passphrase + salt using PBKDF2
const deriveKey = async (
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> => {
  const crypto = getCrypto();

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as Uint8Array<ArrayBuffer>,
      iterations: PBKDF2_ITERATIONS,
      hash: PBKDF2_HASH,
    },
    keyMaterial,
    { name: "AES-GCM", length: KEY_LENGTH * 8 },
    false,
    ["encrypt", "decrypt"],
  );

  return key;
};

/** WebEncryptor
 *
 * AES-GCM encryption/decryption utility for shared Node.js (>=18) and browser environments.
 * Uses the Web Crypto API (crypto.subtle) and works in both frontend and Node.js >=18.
 *
 * Not compatible with Node.js <18 or environments lacking crypto.subtle.
 * Useful for things like HTTP requests since both frontend and backend can use it.
 *
 * @note For backend only encryption/decryption, use packages/shared/backend/src/lib/utils/encryptor.utils.ts
 */
export class WebEncryptor {
  static async encrypt(plaintext: string, passphrase: string): Promise<string> {
    if (!passphrase) throw new Error("Passphrase is required");

    const crypto = getCrypto();

    // random salt & iv
    const salt = new Uint8Array(SALT_LENGTH);
    const iv = new Uint8Array(IV_LENGTH);
    crypto.getRandomValues(salt);
    crypto.getRandomValues(iv);

    const key = await deriveKey(passphrase, salt);

    const ctBuffer = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128,
      },
      key,
      encoder.encode(plaintext),
    );

    const ct = new Uint8Array(ctBuffer);

    // blob = salt | iv | (ciphertext + tag)
    const blob = new Uint8Array(salt.length + iv.length + ct.length);
    blob.set(salt, 0);
    blob.set(iv, salt.length);
    blob.set(ct, salt.length + iv.length);

    return base64UrlEncode(blob);
  }

  static async decrypt(
    cipherText: string,
    passphrase: string,
  ): Promise<string> {
    if (!cipherText || !passphrase)
      throw new Error("Passphrase & cipher are required");

    const crypto = getCrypto();
    const data = base64UrlDecode(cipherText);

    if (data.length < SALT_LENGTH + IV_LENGTH + 1) {
      throw new Error("Ciphertext too short");
    }

    const salt = data.subarray(0, SALT_LENGTH);
    const iv = data.subarray(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ctPlusTag = data.subarray(SALT_LENGTH + IV_LENGTH);

    const key = await deriveKey(passphrase, salt);

    const ptBuffer = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv as Uint8Array<ArrayBuffer>,
        tagLength: 128,
      },
      key,
      ctPlusTag as Uint8Array<ArrayBuffer>,
    );

    return decoder.decode(ptBuffer);
  }
}
